import { useEffect, useRef, useState } from "react";
import { prepopulateFilterFromDefaultValues, generateFilterQueryParams, prepopulateMultiSelectFilterFromDefaultValues, prepopulateMultiSelectFilterValuesFromSearchParams } from "@utils/filtering";
import { usePaginatedApi } from "./useAPI";
import useDetectCurrentCulture from "./useDetectCurrentCulture";
import useBrowserSearchParams from "./useBrowserSearchParams";
const useCardsGrid = ({ filters, pageSize, apiUrl, sortBy, useMultiSelectFilters }) => {
    const { searchParamsObj, urlHasParams, myWindow, setMultiSelectSearchParamsInURL } = useBrowserSearchParams({ useMultiSelectFilters });
    const { culture } = useDetectCurrentCulture();
    const filtersValue = useRef({});
    const [multiSelectFiltersValue, setMultiSelectFiltersValue] = useState({});
    const sortingValue = useRef({});
    const [cards, setCards] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [resetPagination, setResetPagination] = useState(false);
    const { loading: isLoading, currentPage, request
    // http://localhost:3000/  - put it before api/${culture} in case you want to make request to fake-api.
     } = usePaginatedApi(`api/${culture}/${apiUrl}`, {
        initialPage: 1,
        pageSize
    });
    const formatMultiSelectQueryParams = (filtersValues) => {
        const formattedParams = {};
        for (const key in filtersValues) {
            formattedParams[key] = filtersValues[key].map((item) => item.value);
        }
        return formattedParams;
    };
    const initFilterStates = () => {
        if (useMultiSelectFilters) {
            if (urlHasParams) {
                setMultiSelectFiltersValue(prepopulateMultiSelectFilterValuesFromSearchParams(filters, searchParamsObj));
            }
            else {
                const defaultFilters = prepopulateMultiSelectFilterFromDefaultValues(filters);
                const hasDefaultFilters = defaultFilters && Object.keys(defaultFilters).length !== 0;
                hasDefaultFilters && setMultiSelectFiltersValue(defaultFilters);
                hasDefaultFilters && setMultiSelectSearchParamsInURL(formatMultiSelectQueryParams(defaultFilters));
            }
        }
        else {
            filtersValue.current = urlHasParams ? searchParamsObj : prepopulateFilterFromDefaultValues(filters);
            sortingValue.current = urlHasParams && (searchParamsObj === null || searchParamsObj === void 0 ? void 0 : searchParamsObj.sortBy) ? { sortBy: searchParamsObj.sortBy } : prepopulateFilterFromDefaultValues([sortBy]);
        }
    };
    useEffect(() => {
        if (myWindow) {
            initFilterStates();
        }
    }, [searchParamsObj, urlHasParams, myWindow]);
    const getAndSetListItems = async (page, shouldMerge = false, additionalParams = null) => {
        const { data, totalCount: fetchedTotalCount } = (await request({
            params: { ...(filtersValue.current || null), ...(sortingValue.current || null), ...additionalParams, currentPage: page || 1, pageSize }
        }));
        setCards((prevState) => (shouldMerge ? [...prevState, ...data] : data));
        setTotalCount(fetchedTotalCount);
    };
    useEffect(() => {
        var _a;
        culture && getAndSetListItems((_a = searchParamsObj === null || searchParamsObj === void 0 ? void 0 : searchParamsObj.pageNumber) === null || _a === void 0 ? void 0 : _a[0], false, searchParamsObj);
    }, [culture]);
    useEffect(() => {
        setHasMore((cards === null || cards === void 0 ? void 0 : cards.length) < totalCount && (cards === null || cards === void 0 ? void 0 : cards.length) !== 0);
    }, [cards, totalCount]);
    const handleShowMore = async (event) => {
        event.preventDefault();
        await getAndSetListItems(currentPage + 1, true);
    };
    const handleSelectorChanges = async (appliedFilters, ref) => {
        ref.current = generateFilterQueryParams(appliedFilters);
        await getAndSetListItems(1);
    };
    const handleMuliSelectorChanges = async (appliedFilters, filterName) => {
        const newFiltersValues = {
            ...multiSelectFiltersValue,
            [filterName]: appliedFilters
        };
        const formattedParams = formatMultiSelectQueryParams(newFiltersValues);
        setMultiSelectFiltersValue(newFiltersValues);
        setMultiSelectSearchParamsInURL({ ...formattedParams, pageNumber: [String(currentPage)] });
        setResetPagination(true);
        getAndSetListItems(currentPage, false, formattedParams);
    };
    const handlePageChanges = async (pageNumber) => {
        if (pageNumber !== currentPage) {
            setMultiSelectSearchParamsInURL({ ...formatMultiSelectQueryParams(multiSelectFiltersValue), pageNumber: [String(pageNumber)] });
            setResetPagination(false);
            getAndSetListItems(pageNumber, false, formatMultiSelectQueryParams(multiSelectFiltersValue));
        }
    };
    const handleResetBtn = (event) => {
        event.preventDefault();
        setResetPagination(true);
        setMultiSelectFiltersValue({});
        getAndSetListItems(1);
        setMultiSelectSearchParamsInURL({});
    };
    return {
        handleShowMore,
        handleSelectorChanges,
        handleMuliSelectorChanges,
        handlePageChanges,
        handleResetBtn,
        hasMore,
        isLoading,
        filtersValue,
        multiSelectFiltersValue,
        sortingValue,
        cards,
        totalCount,
        resetPagination,
        currentPage
    };
};
export default useCardsGrid;
